import { useCallback, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useResponsiveStore } from "@/stores/editor/responsive-store";
import { useToast } from "@/hooks/use-toast";

export interface KeyboardShortcut {
  keys: string;
  description: string;
  action: () => void;
  enabled?: boolean;
  category: "editing" | "selection" | "navigation" | "tools";
}

export interface KeyboardShortcutsConfig {
  enableTooltips?: boolean;
  showHelpDialog?: boolean;
  customShortcuts?: KeyboardShortcut[];
}

export const useKeyboardShortcuts = (config: KeyboardShortcutsConfig = {}) => {
  const {
    undo,
    redo,
    canUndo,
    canRedo,
    getSelectedComponents,
    selectComponent,
    selectMultipleComponents,
    clearSelection,
    deleteComponent,
    copyComponents,
    cutComponents,
    pasteComponents,
    duplicateComponents,
    selectedComponents,
    toggleGrid,
    toggleSnapToGrid,
    togglePreviewMode,
  } = useEditorStore();

  // Week 2: Use responsive store's zoom functions for dual scale system
  const zoomIn = useResponsiveStore((state) => state.zoomIn);
  const zoomOut = useResponsiveStore((state) => state.zoomOut);
  const resetZoom = useResponsiveStore((state) => state.resetZoom);
  const userZoom = useResponsiveStore((state) => state.userZoom);

  const { toast } = useToast();
  const isMultiSelectMode = useRef(false);
  const lastSelectedComponent = useRef<string | null>(null);

  const hasActiveTextSelection = useCallback(() => {
    const { textSelection } = useEditorStore.getState();
    return Boolean(
      textSelection &&
      textSelection.componentId &&
      textSelection.startIndex !== textSelection.endIndex
    );
  }, []);

  // Enhanced multi-select functionality
  const handleMultiSelect = useCallback(
    (componentId: string, mode: "add" | "range" | "toggle") => {
      const currentSelected = selectedComponents;

      switch (mode) {
        case "add":
          if (!currentSelected.includes(componentId)) {
            selectMultipleComponents([...currentSelected, componentId]);
          }
          break;

        case "toggle":
          if (currentSelected.includes(componentId)) {
            selectMultipleComponents(
              currentSelected.filter((id) => id !== componentId),
            );
          } else {
            selectMultipleComponents([...currentSelected, componentId]);
          }
          break;

        case "range":
          // Implement range selection logic here
          // This would need component ordering/hierarchy information
          if (
            lastSelectedComponent.current &&
            lastSelectedComponent.current !== componentId
          ) {
            // For now, just add to selection
            if (!currentSelected.includes(componentId)) {
              selectMultipleComponents([...currentSelected, componentId]);
            }
          } else {
            selectComponent(componentId);
          }
          break;
      }

      lastSelectedComponent.current = componentId;
    },
    [selectedComponents, selectComponent, selectMultipleComponents],
  );

  // Batch operations for multiple components
  const performBatchOperation = useCallback(
    (operation: "delete" | "copy" | "cut" | "duplicate") => {
      const { textEditingComponentId } = useEditorStore.getState();
      if (textEditingComponentId !== null || hasActiveTextSelection()) {
        return;
      }

      const activeElement = document.activeElement as HTMLElement | null;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.isContentEditable)
      ) {
        return;
      }

      const selected = getSelectedComponents();
      if (selected.length === 0) {
        toast({
          title: "No Selection",
          description: "Please select one or more components first.",
          variant: "default",
        });
        return;
      }

      const selectedIds = selected.map((comp) => comp.id);

      switch (operation) {
        case "delete":
          selectedIds.forEach((id) => deleteComponent(id));
          toast({
            title: "Components Deleted",
            description: `Deleted ${selectedIds.length} component${selectedIds.length > 1 ? "s" : ""}`,
          });
          break;

        case "copy":
          copyComponents(selectedIds);
          toast({
            title: "Components Copied",
            description: `Copied ${selectedIds.length} component${selectedIds.length > 1 ? "s" : ""} to clipboard`,
          });
          break;

        case "cut":
          cutComponents(selectedIds);
          toast({
            title: "Components Cut",
            description: `Cut ${selectedIds.length} component${selectedIds.length > 1 ? "s" : ""} to clipboard`,
          });
          break;

        case "duplicate":
          duplicateComponents(selectedIds);
          toast({
            title: "Components Duplicated",
            description: `Duplicated ${selectedIds.length} component${selectedIds.length > 1 ? "s" : ""}`,
          });
          break;
      }
    },
    [
      getSelectedComponents,
      deleteComponent,
      copyComponents,
      cutComponents,
      duplicateComponents,
      toast,
      hasActiveTextSelection,
    ],
  );

  // Navigation helpers
  const selectAllComponents = useCallback(() => {
    const { components } = useEditorStore.getState();
    const allIds = components.map((comp) => comp.id);
    selectMultipleComponents(allIds);
    toast({
      title: "All Selected",
      description: `Selected all ${allIds.length} components`,
    });
  }, [selectMultipleComponents, toast]);

  const invertSelection = useCallback(() => {
    const { components, selectedComponents } = useEditorStore.getState();
    const allIds = components.map((comp) => comp.id);
    const newSelection = allIds.filter(
      (id) => !selectedComponents.includes(id),
    );
    selectMultipleComponents(newSelection);
    toast({
      title: "Selection Inverted",
      description: `Selected ${newSelection.length} components`,
    });
  }, [selectMultipleComponents, toast]);

  // Define all keyboard shortcuts
  const shortcuts: KeyboardShortcut[] = [
    // Editing shortcuts
    {
      keys: "ctrl+z, cmd+z",
      description: "Undo last action",
      action: () => {
        if (canUndo()) {
          undo();
          toast({ title: "Undone", description: "Last action undone" });
        }
      },
      enabled: canUndo(),
      category: "editing",
    },
    {
      keys: "ctrl+y, cmd+y, ctrl+shift+z, cmd+shift+z",
      description: "Redo last undone action",
      action: () => {
        if (canRedo()) {
          redo();
          toast({ title: "Redone", description: "Action redone" });
        }
      },
      enabled: canRedo(),
      category: "editing",
    },
    {
      keys: "delete, backspace",
      description: "Delete selected components",
      action: () => performBatchOperation("delete"),
      enabled: selectedComponents.length > 0,
      category: "editing",
    },
    {
      keys: "ctrl+c, cmd+c",
      description: "Copy selected components",
      action: () => performBatchOperation("copy"),
      enabled: selectedComponents.length > 0,
      category: "editing",
    },
    {
      keys: "ctrl+x, cmd+x",
      description: "Cut selected components",
      action: () => performBatchOperation("cut"),
      enabled: selectedComponents.length > 0,
      category: "editing",
    },
    {
      keys: "ctrl+v, cmd+v",
      description: "Paste components from clipboard",
      action: () => {
        pasteComponents();
        toast({
          title: "Pasted",
          description: "Components pasted from clipboard",
        });
      },
      category: "editing",
    },
    {
      keys: "ctrl+d, cmd+d",
      description: "Duplicate selected components",
      action: () => performBatchOperation("duplicate"),
      enabled: selectedComponents.length > 0,
      category: "editing",
    },

    // Selection shortcuts
    {
      keys: "ctrl+a, cmd+a",
      description: "Select all components",
      action: selectAllComponents,
      category: "selection",
    },
    {
      keys: "ctrl+shift+a, cmd+shift+a",
      description: "Invert selection",
      action: invertSelection,
      category: "selection",
    },
    {
      keys: "escape",
      description: "Clear selection",
      action: () => {
        clearSelection();
        toast({
          title: "Selection Cleared",
          description: "All components deselected",
        });
      },
      enabled: selectedComponents.length > 0,
      category: "selection",
    },

    // Navigation and view shortcuts (Week 2: Updated to use responsive store zoom)
    {
      keys: "ctrl+plus, cmd+plus, ctrl+=, cmd+=",
      description: "Zoom in (user zoom)",
      action: () => {
        zoomIn();
        const newZoom = useResponsiveStore.getState().userZoom;
        toast({
          title: "Zoomed In",
          description: `User zoom: ${Math.round(newZoom * 100)}%`
        });
      },
      category: "navigation",
    },
    {
      keys: "ctrl+minus, cmd+minus",
      description: "Zoom out (user zoom)",
      action: () => {
        zoomOut();
        const newZoom = useResponsiveStore.getState().userZoom;
        toast({
          title: "Zoomed Out",
          description: `User zoom: ${Math.round(newZoom * 100)}%`
        });
      },
      category: "navigation",
    },
    {
      keys: "ctrl+0, cmd+0",
      description: "Reset zoom to 100%",
      action: () => {
        resetZoom();
        toast({
          title: "Zoom Reset",
          description: "User zoom reset to 100%",
        });
      },
      category: "navigation",
    },

    // Tools shortcuts
    {
      keys: "ctrl+g, cmd+g",
      description: "Toggle grid visibility",
      action: () => {
        toggleGrid();
        const { gridEnabled } = useEditorStore.getState();
        toast({
          title: "Grid Toggled",
          description: `Grid ${gridEnabled ? "enabled" : "disabled"}`,
        });
      },
      category: "tools",
    },
    {
      keys: "ctrl+shift+g, cmd+shift+g",
      description: "Toggle snap to grid",
      action: () => {
        toggleSnapToGrid();
        const { snapToGrid } = useEditorStore.getState();
        toast({
          title: "Snap to Grid Toggled",
          description: `Snap to grid ${snapToGrid ? "enabled" : "disabled"}`,
        });
      },
      category: "tools",
    },
    {
      keys: "ctrl+p, cmd+p",
      description: "Toggle preview mode",
      action: () => {
        togglePreviewMode();
        const { isPreviewMode } = useEditorStore.getState();
        toast({
          title: "Preview Mode Toggled",
          description: `Preview mode ${isPreviewMode ? "enabled" : "disabled"}`,
        });
      },
      category: "tools",
    },

    // Help shortcut
    {
      keys: "ctrl+/, cmd+/, f1",
      description: "Show keyboard shortcuts help",
      action: () => {
        // This would open a help dialog with all shortcuts
        toast({
          title: "Keyboard Shortcuts",
          description: "Press Ctrl+Shift+? to see all shortcuts",
        });
      },
      category: "tools",
    },

    // Add any custom shortcuts from config
    ...(config.customShortcuts || []),
  ];

  // Register all shortcuts individually to avoid hooks in loop
  const enabledShortcuts = shortcuts.filter((s) => s.enabled !== false);

  // Register editing shortcuts
  useHotkeys(
    "ctrl+z, cmd+z",
    (e) => {
      e.preventDefault();
      if (canUndo()) {
        undo();
        toast({ title: "Undone", description: "Last action undone" });
      }
    },
    { enableOnFormTags: false },
  );
  useHotkeys(
    "ctrl+y, cmd+y, ctrl+shift+z, cmd+shift+z",
    (e) => {
      e.preventDefault();
      if (canRedo()) {
        redo();
        toast({ title: "Redone", description: "Action redone" });
      }
    },
    { enableOnFormTags: false },
  );
  useHotkeys(
    "delete, backspace",
    (e) => {
      // Don't handle if text is being edited inline
      if (useEditorStore.getState().textEditingComponentId !== null) return;

      // Don't handle if a text range is selected inside a text component
      if (hasActiveTextSelection()) return;

      // Don't handle if user is typing in a form field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable) {
        return; // Let the input handle it
      }
      e.preventDefault();
      performBatchOperation("delete");
    },
    { enableOnFormTags: false },
  );
  useHotkeys(
    "ctrl+c, cmd+c",
    (e) => {
      e.preventDefault();
      performBatchOperation("copy");
    },
    { enableOnFormTags: false },
  );
  useHotkeys(
    "ctrl+x, cmd+x",
    (e) => {
      e.preventDefault();
      performBatchOperation("cut");
    },
    { enableOnFormTags: false },
  );
  useHotkeys(
    "ctrl+v, cmd+v",
    (e) => {
      e.preventDefault();
      pasteComponents();
      toast({
        title: "Pasted",
        description: "Components pasted from clipboard",
      });
    },
    { enableOnFormTags: false },
  );
  useHotkeys(
    "ctrl+d, cmd+d",
    (e) => {
      e.preventDefault();
      performBatchOperation("duplicate");
    },
    { enableOnFormTags: false },
  );

  // Register selection shortcuts
  useHotkeys(
    "ctrl+a, cmd+a",
    (e) => {
      e.preventDefault();
      selectAllComponents();
    },
    { enableOnFormTags: false },
  );
  useHotkeys(
    "ctrl+shift+a, cmd+shift+a",
    (e) => {
      e.preventDefault();
      invertSelection();
    },
    { enableOnFormTags: false },
  );
  useHotkeys(
    "escape",
    (e) => {
      e.preventDefault();
      clearSelection();
      toast({
        title: "Selection Cleared",
        description: "All components deselected",
      });
    },
    { enableOnFormTags: false },
  );

  // Register navigation shortcuts (Week 2: Using responsive store zoom)
  useHotkeys(
    "ctrl+plus, cmd+plus, ctrl+=, cmd+=",
    (e) => {
      e.preventDefault();
      zoomIn();
      const newZoom = useResponsiveStore.getState().userZoom;
      toast({
        title: "Zoomed In",
        description: `User zoom: ${Math.round(newZoom * 100)}%`
      });
    },
    { enableOnFormTags: false },
  );
  useHotkeys(
    "ctrl+minus, cmd+minus",
    (e) => {
      e.preventDefault();
      zoomOut();
      const newZoom = useResponsiveStore.getState().userZoom;
      toast({
        title: "Zoomed Out",
        description: `User zoom: ${Math.round(newZoom * 100)}%`
      });
    },
    { enableOnFormTags: false },
  );
  useHotkeys(
    "ctrl+0, cmd+0",
    (e) => {
      e.preventDefault();
      resetZoom();
      toast({ title: "Zoom Reset", description: "User zoom reset to 100%" });
    },
    { enableOnFormTags: false },
  );

  // Register tool shortcuts
  useHotkeys(
    "ctrl+g, cmd+g",
    (e) => {
      e.preventDefault();
      toggleGrid();
      const { gridEnabled } = useEditorStore.getState();
      toast({
        title: "Grid Toggled",
        description: `Grid ${gridEnabled ? "enabled" : "disabled"}`,
      });
    },
    { enableOnFormTags: false },
  );
  useHotkeys(
    "ctrl+shift+g, cmd+shift+g",
    (e) => {
      e.preventDefault();
      toggleSnapToGrid();
      const { snapToGrid } = useEditorStore.getState();
      toast({
        title: "Snap to Grid Toggled",
        description: `Snap to grid ${snapToGrid ? "enabled" : "disabled"}`,
      });
    },
    { enableOnFormTags: false },
  );
  useHotkeys(
    "ctrl+p, cmd+p",
    (e) => {
      e.preventDefault();
      togglePreviewMode();
      const { isPreviewMode } = useEditorStore.getState();
      toast({
        title: "Preview Mode Toggled",
        description: `Preview mode ${isPreviewMode ? "enabled" : "disabled"}`,
      });
    },
    { enableOnFormTags: false },
  );

  // Register help shortcut
  useHotkeys(
    "ctrl+/, cmd+/, f1",
    (e) => {
      e.preventDefault();
      toast({
        title: "Keyboard Shortcuts",
        description: "Press Ctrl+Shift+? to see all shortcuts",
      });
    },
    { enableOnFormTags: false },
  );

  // Track multi-select mode
  useHotkeys(
    "ctrl, cmd",
    () => {
      isMultiSelectMode.current = true;
    },
    { keyup: true },
    [],
  );

  useHotkeys(
    "ctrl, cmd",
    () => {
      isMultiSelectMode.current = false;
    },
    { keydown: false, keyup: true },
    [],
  );

  // Return utilities for components to use
  return {
    shortcuts,
    isMultiSelectMode: isMultiSelectMode.current,
    handleMultiSelect,
    performBatchOperation,
    selectAllComponents,
    invertSelection,
  };
};

// Hook for displaying shortcuts help
export const useShortcutsHelp = () => {
  const { shortcuts } = useKeyboardShortcuts();

  const groupedShortcuts = shortcuts.reduce(
    (groups, shortcut) => {
      const category = shortcut.category;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(shortcut);
      return groups;
    },
    {} as Record<string, KeyboardShortcut[]>,
  );

  const formatKeys = (keys: string) => {
    return keys
      .split(", ")[0] // Take first key combination
      .replace(/ctrl/g, "⌃")
      .replace(/cmd/g, "⌘")
      .replace(/shift/g, "⇧")
      .replace(/alt/g, "⌥")
      .replace(/\+/g, " + ")
      .toUpperCase();
  };

  return {
    groupedShortcuts,
    formatKeys,
    allShortcuts: shortcuts,
  };
};
